import React, { useState } from "react";
import { api, API_BASE } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Download, Trash2, ShieldAlert, FileJson, FileSpreadsheet, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

/**
 * GDPR rights panel — reusable for both Company and Manager settings.
 * Provides:
 *  - Export my data (JSON / CSV)
 *  - Delete my account (with confirmation)
 */
export const GdprPanel = () => {
  const { logout } = useAuth();
  const [busy, setBusy] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const token = localStorage.getItem("cxoora_token") || "";

  const downloadExport = (fmt) => {
    const url = `${API_BASE}/gdpr/export?fmt=${fmt}&auth=${encodeURIComponent(token)}`;
    window.open(url, "_blank", "noopener");
    toast.success(`Esportazione ${fmt.toUpperCase()} avviata`);
  };

  const deleteAccount = async () => {
    setBusy(true);
    try {
      const r = await api.delete("/gdpr/delete-account");
      toast.success(r.data?.message || "Account cancellato");
      setTimeout(() => { logout(); window.location.href = "/"; }, 1500);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="cxo-card p-6 mt-4 space-y-4 border-l-4 border-l-[#B7791F]" data-testid="gdpr-panel">
      <div className="flex items-start gap-3">
        <div className="rounded-xl bg-[#FFF6E6] p-2.5 text-[#B7791F] shrink-0">
          <ShieldAlert size={20} />
        </div>
        <div>
          <h3 className="font-display text-base font-semibold text-slate-900">I miei dati personali (GDPR)</h3>
          <p className="text-sm text-slate-600 mt-0.5">
            Esercita i tuoi diritti previsti dal Regolamento UE 2016/679 (artt. 15, 17, 20 GDPR).
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2">
        <Button
          type="button"
          variant="outline"
          onClick={() => downloadExport("json")}
          className="justify-start gap-2"
          data-testid="gdpr-export-json-btn"
        >
          <FileJson size={16} className="text-[#2D6BFF]" />
          <span className="flex-1 text-left">
            <span className="block text-sm font-medium">Esporta i miei dati (JSON)</span>
            <span className="block text-xs text-slate-500">Formato strutturato — Art. 20 GDPR</span>
          </span>
        </Button>
        <Button
          type="button"
          variant="outline"
          onClick={() => downloadExport("csv")}
          className="justify-start gap-2"
          data-testid="gdpr-export-csv-btn"
        >
          <FileSpreadsheet size={16} className="text-[#1F9D6A]" />
          <span className="flex-1 text-left">
            <span className="block text-sm font-medium">Riepilogo CSV</span>
            <span className="block text-xs text-slate-500">Riepilogo aggregato per categoria</span>
          </span>
        </Button>
      </div>

      <div className="pt-3 border-t border-slate-100">
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button variant="outline" className="text-red-600 hover:text-red-700 border-red-200 hover:border-red-300 gap-1.5" data-testid="gdpr-delete-trigger">
              <Trash2 size={14} /> Cancella il mio account
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent data-testid="gdpr-delete-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle className="text-red-700">Confermi la cancellazione dell&apos;account?</AlertDialogTitle>
              <AlertDialogDescription className="space-y-2">
                <span className="block">
                  Eserciterai il <strong>diritto all&apos;oblio</strong> (Art. 17 GDPR). Il tuo account verrà disattivato immediatamente e
                  cancellato definitivamente entro <strong>30 giorni</strong>. I dati pseudonimizzati conservati per obblighi fiscali
                  (10 anni) saranno conservati senza informazioni identificative.
                </span>
                <span className="block text-xs text-slate-500 mt-2">
                  Per confermare, scrivi <code className="bg-slate-100 px-1.5 py-0.5 rounded text-red-600 font-mono">ELIMINA</code> nel campo qui sotto:
                </span>
                <input
                  type="text"
                  value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value)}
                  placeholder="Scrivi ELIMINA"
                  className="mt-2 w-full px-3 py-2 rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-red-500 text-sm font-mono"
                  data-testid="gdpr-delete-confirm-input"
                />
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel onClick={() => setConfirmText("")} data-testid="gdpr-delete-cancel">Annulla</AlertDialogCancel>
              <AlertDialogAction
                disabled={confirmText !== "ELIMINA" || busy}
                onClick={deleteAccount}
                className="bg-red-600 hover:bg-red-700 disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="gdpr-delete-confirm-btn"
              >
                {busy && <Loader2 className="animate-spin mr-2" size={14} />}
                Cancella definitivamente
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
        <p className="text-xs text-slate-500 mt-2">
          Per limitare o opporti al trattamento dei dati senza cancellare l&apos;account, scrivi a{" "}
          <a href="mailto:privacy@cxoora.com" className="text-[#2D6BFF] hover:underline">privacy@cxoora.com</a>.
        </p>
      </div>
    </div>
  );
};

/**
 * Admin Legal Dossier panel — download PDF/DOCX for the consulente legale.
 */
export const LegalDossierPanel = () => {
  const [meta, setMeta] = useState(null);
  const token = localStorage.getItem("cxoora_token") || "";

  React.useEffect(() => {
    api.get("/legal/dossier/meta").then((r) => setMeta(r.data)).catch(() => {});
  }, []);

  const download = (format) => {
    const url = `${API_BASE}/legal/dossier/${format}?auth=${encodeURIComponent(token)}`;
    window.open(url, "_blank", "noopener");
    toast.success(`Download ${format.toUpperCase()} avviato`);
  };

  return (
    <div className="cxo-card p-6" data-testid="legal-dossier-panel">
      <h2 className="font-display text-xl font-semibold text-slate-900">Dossier per il consulente legale</h2>
      <p className="text-sm text-slate-600 mt-1">
        Documento di lavoro completo pronto da consegnare al tuo studio legale per la revisione di privacy, termini, codice etico e contratti.
      </p>

      {meta && (
        <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3">
          <Stat label="Sezioni" value={meta.total_sections} />
          <Stat label="Domande compliance" value={meta.total_questions} />
          <Stat label="Versione" value={meta.version?.split(" ")[0] || "v1.0"} />
          <Stat label="Lingua" value="IT" />
        </div>
      )}

      <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Button onClick={() => download("pdf")} className="bg-[#2D6BFF] hover:bg-[#1F57F2] gap-1.5" data-testid="download-dossier-pdf-btn">
          <Download size={14} /> Scarica PDF (per consegna)
        </Button>
        <Button onClick={() => download("docx")} variant="outline" className="gap-1.5" data-testid="download-dossier-docx-btn">
          <Download size={14} /> Scarica DOCX (track changes)
        </Button>
      </div>

      {meta?.sections && (
        <div className="mt-5">
          <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">Cosa contiene il dossier</p>
          <ol className="space-y-1.5 text-sm text-slate-700">
            {meta.sections.map((s, i) => (
              <li key={i} className="flex items-start gap-2">
                <span className="text-[#2D6BFF] font-semibold tabular-nums w-5">{i + 1}.</span>
                <span>{s.replace(/^\d+\.\s*/, "")}</span>
              </li>
            ))}
          </ol>
        </div>
      )}

      <div className="mt-5 p-3 rounded-lg bg-[#FFF6E6] border border-[#B7791F]/30 text-xs text-[#8B5E12]">
        💡 <strong>Suggerimento:</strong> Scarica entrambi i formati. Invia il PDF come copia di sicurezza, il DOCX al legale per le revisioni con track changes.
      </div>
    </div>
  );
};

const Stat = ({ label, value }) => (
  <div className="rounded-lg bg-slate-50 p-3">
    <p className="text-xs uppercase tracking-wider text-slate-500">{label}</p>
    <p className="font-display text-xl font-bold cxo-tabular text-slate-900 mt-1">{value}</p>
  </div>
);
